import { planFraming, sameFraming, sourceFromOutput, type CameraFraming, type FrameSize } from "./camera-framing";

/**
 * L'aperçu d'un cadrage : ce qu'on sait de l'image pendant que le cadrage
 * voyage, et ce qu'on retient d'un geste avant de le publier.
 *
 * Deux régimes, parce que les deux caméras d'un récital ne se ressemblent pas :
 *
 * - d'un **appareil appairé**, on ne reçoit que ce qu'il a déjà rogné. Le geste
 *   se dessine sur ces pixels-là, la donnée part amortie, et il faut deviner à
 *   quel moment les pixels reçus montrent enfin le cadrage publié : c'est
 *   {@link shownAfter} ;
 * - la **webcam de ce poste** est là, entière. Le geste se dessine sur son
 *   image telle qu'elle filme, rien ne part avant qu'on valide, et il faut
 *   tenir le brouillon : c'est {@link draftReducer}.
 *
 * Rien ici ne touche à ce qui sort de l'appareil : c'est de l'affichage, chez
 * qui cadre. Voir `docs/spec/video-engine.md`, « Cadrage ».
 */

/**
 * Ce que l'image reçue montre : un cadrage, et la définition qu'elle avait en
 * le montrant.
 *
 * Les deux vont ensemble, et c'est tout le point. Le 2026-09-20, la définition
 * fraîchement reçue était lue avec le cadrage d'avant : l'image passait de
 * 1280×720 à 640×360, on en déduisait une caméra de 640×360, on concluait que
 * les pixels n'étaient pas arrivés, et la transformation du geste posait un
 * `scale(2)` sur une image déjà rognée de moitié — quatre fois trop gros, une
 * demi-seconde, puis un saut.
 */
export interface ShownFraming {
  readonly framing: CameraFraming;
  /** La définition de l'image quand elle montrait ce cadrage ; zéro tant qu'aucune image n'est arrivée. */
  readonly frame: FrameSize;
}

/**
 * Au bout de combien de temps, en ms, on tient pour acquis qu'un cadrage
 * publié est dans les pixels.
 *
 * Un déplacement ne change ni la taille ni la forme de l'image : rien ne dit
 * quand il est arrivé, et ce délai est sa seule réponse. Il couvre le trajet
 * jusqu'à l'appareil, sa toile, son encodeur et le retour.
 */
export const PAN_ARRIVAL_MS = 500;

/**
 * Le filet pour un cadrage qui **doit** changer la définition reçue — un zoom,
 * un redressement, un quart de tour — et dont le changement n'arrive pas.
 *
 * Plus long que {@link PAN_ARRIVAL_MS}, parce qu'on a mieux qu'un délai pour
 * ces cadrages-là : la définition qui change. Le filet ne sert que si elle ne
 * change pas — un appareil resté sur une version qui ignore le cadrage, ou une
 * couche de salle qui donne par hasard la même taille —, et il rend alors
 * l'image à ce qu'elle publie vraiment, plutôt que de la laisser à jamais sous
 * une transformation qui ment.
 */
export const RESIZE_ARRIVAL_MS = 1500;

/**
 * Ce que l'image montre après une image reçue, ou une métadonnée.
 *
 * # Un changement de définition, c'est un changement de pixels
 *
 * Si on attendait un cadrage — celui que la séance publie n'est pas celui que
 * l'image montre —, c'est lui qui vient d'arriver : la toile de l'appareil a
 * changé de taille, et l'encodeur avec elle. Si on n'attendait rien, c'est la
 * caméra qui a changé de forme — un téléphone qu'on tourne — ou la salle qui a
 * changé de couche ; ce que l'image montre n'a pas bougé, seule sa définition
 * a changé, et c'est elle qu'on retient pour la suite du calcul.
 *
 * # Ce que ça ne sait pas distinguer
 *
 * Une couche de salle qui change pendant qu'on attend un cadrage : la salle
 * passe de 720p à 360p sous la contrainte, et on tient les pixels pour arrivés
 * alors qu'ils montrent encore l'ancien cadrage. L'aperçu redevient alors
 * l'image reçue telle quelle — l'ancien cadrage, sans transformation —, le
 * temps que le vrai arrive : un flottement, pas une image quatre fois trop
 * grosse. On sait qu'on est en avant, donc en couche haute ; le reste est le
 * prix d'un canal qui ne dit rien de plus que la taille de l'image.
 */
export function shownAfter(shown: ShownFraming, published: CameraFraming, frame: FrameSize): ShownFraming {
  if (frame.width <= 0 || frame.height <= 0 || sameSize(shown.frame, frame)) {
    return shown;
  }

  const behind = !sameFraming(shown.framing, published);

  return { framing: behind ? published : shown.framing, frame };
}

/**
 * Le délai au bout duquel on tient le cadrage publié pour arrivé, quand la
 * définition ne l'a pas dit : court pour un déplacement, qui ne change rien de
 * mesurable ; long pour ce qui devrait changer la définition, et ne l'a pas
 * encore fait.
 */
export function arrivalDelayMs(shown: ShownFraming, published: CameraFraming): number {
  const source = sourceFromOutput(shown.frame, shown.framing);
  const before = planFraming(source, shown.framing).output;
  const after = planFraming(source, published).output;

  return nearSize(before, after) ? PAN_ARRIVAL_MS : RESIZE_ARRIVAL_MS;
}

function sameSize(a: FrameSize, b: FrameSize): boolean {
  return a.width === b.width && a.height === b.height;
}

/** À deux pixels près : l'arrondi aux dimensions paires ne vaut pas une attente. */
function nearSize(a: FrameSize, b: FrameSize): boolean {
  return Math.abs(a.width - b.width) <= 2 && Math.abs(a.height - b.height) <= 2;
}

/**
 * Le brouillon d'un cadrage local, du premier geste à sa publication.
 *
 * - `idle` : rien en cours, l'aperçu montre ce que la séance publie ;
 * - `editing` : un cadrage en train de se faire, que personne d'autre ne voit ;
 * - `sent` : validé et parti, en attendant que la séance le publie — l'aperçu
 *   le garde, pour ne pas revenir à l'ancien le temps de l'aller-retour.
 */
export type FramingDraft =
  | { readonly stage: "idle" }
  | { readonly stage: "editing"; readonly framing: CameraFraming }
  | { readonly stage: "sent"; readonly framing: CameraFraming };

export type FramingDraftAction =
  /** Un geste, ou un bouton : le brouillon prend cette valeur. */
  | { readonly type: "gesture"; readonly framing: CameraFraming }
  /** « Valider » : le brouillon part. */
  | { readonly type: "validate" }
  /** « Annuler », ou un envoi refusé, ou un envoi qui n'est jamais revenu : retour à ce qui est publié. */
  | { readonly type: "discard" }
  /** La séance publie un cadrage. */
  | { readonly type: "published"; readonly framing: CameraFraming };

export const IDLE_DRAFT: FramingDraft = { stage: "idle" };

/**
 * Le brouillon après un événement.
 *
 * Un geste pendant l'attente rouvre le brouillon : on ne fait pas attendre un
 * doigt. Et la séance qui publie **ce qu'on avait envoyé** clôt l'attente ; ce
 * qu'elle publie d'autre pendant qu'on édite ne touche pas au brouillon — un
 * autre onglet qui recadre n'a pas à défaire un geste en cours, et le brouillon
 * l'emportera ou s'abandonnera.
 */
export function draftReducer(state: FramingDraft, action: FramingDraftAction): FramingDraft {
  switch (action.type) {
    case "gesture":
      return { stage: "editing", framing: action.framing };

    case "validate":
      return state.stage === "editing" ? { stage: "sent", framing: state.framing } : state;

    case "discard":
      return IDLE_DRAFT;

    case "published":
      return state.stage === "sent" && sameFraming(state.framing, action.framing) ? IDLE_DRAFT : state;
  }
}

/** Le cadrage que l'aperçu montre : le brouillon s'il y en a un, ce qui est publié sinon. */
export function draftFraming(state: FramingDraft, published: CameraFraming): CameraFraming {
  return state.stage === "idle" ? published : state.framing;
}
